import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fadeOut } from 'src/app/animations';
import { NavigationActionButton } from 'src/app/components/navigation-bar/models/navigation-action-button.model';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { GlobalUtilsService } from 'src/app/services/ui/global-utils.service';
import { ButtonIcons } from 'src/app/components/action-button/models/button-icons.enum';
import { GenericModalComponent } from 'src/app/components/generic-modal/generic-modal.component';
import { DigitalLogService } from 'src/app/services/sections/job/digital-log.service';
import { DocumentsService } from 'src/app/services/sections/documents/documents.service';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocViewerDocumentDetails, DocumentViewerComponent } from 'src/app/components/document-viewer/document-viewer.component';
import { DigitalLogFormPhoto } from 'src/app/models/digital-log/digital-log.model';
import moment from 'moment';
import { ToastType } from 'src/app/components/toast/models/toast-type.enum';
import { ToastService } from 'src/app/services/ui/toast.service';

import { sortBy } from 'lodash-es';

@Component({
	templateUrl: './digital-log-form-photos.component.html',
	styleUrls: ['./digital-log-form-photos.component.scss', '../../../../stylesheets/job-main.scss', '../../../../stylesheets/job-actions.scss', '../../../../stylesheets/job-inputs.scss', '../../../../stylesheets/job-form.scss'],
	animations: [fadeOut(700)]
})
export class DigitalLogFormPhotosComponent implements OnInit, OnDestroy {
	photos: DigitalLogFormPhoto[] = [];
	filteredPhotos: DigitalLogFormPhoto[] = [];
	public externalCloseEventEmitter = new Subject<void>();
	public search: string;
	public fromDate: any;
	public toDate: any;
	public form;
	public self = this;
	@ViewChild('photoDetailsModal', { static: true }) public detailsModal: GenericModalComponent;
	@ViewChild('addPhotosConfirmationModal', { static: true }) public addPhotosConfirmationModal: GenericModalComponent;
	@ViewChild('deleteConfirmationModal', { static: true }) public deleteConfirmationModal: GenericModalComponent;
	@ViewChild('photoPreviewModal', { static: true }) public photoPreviewModal: GenericModalComponent;
	@ViewChild('photoPreviewViewer') private photoPreviewViewer?: DocumentViewerComponent;
	public readonly ButtonIcons = ButtonIcons;

	public digitalLogFormFields = [];

	public groupedDigitalLogFormFields = [];
	public availableGroupedDigitalLogFormFields = [];
	public isDeleteDisabled = false;
	public isPhotosSaving = false;
	public addPhotosMessage = '';
	public selectedDocumentDetails: DocViewerDocumentDetails;
	public onPhotoPreviewModalHide = () => this.handlePhotoPreviewModalHide();
	public onPhotoPreviewViewerRendered = (uiInstance: any) => this.handlePhotoPreviewViewerRendered(uiInstance);

	private unsubscriber$ = new Subject<void>();
	private dateFormat = 'M/D/YYYY';
	public selectedGroupId: number;
	private fallbackFileBaseName = 'Photo';
	private photoPreviewRequest$ = new Subject<void>();
	private photoViewerUiInstance: any;
	private photoViewerDocumentLoadedHandler = () => {
		this.ngZone.run(() => {
			this.guardPhotoViewerTextRequests();
			this.ensureMobileFitMode();
			this.applyMobilePhotoPreviewUiOverrides();
		});
	};
	private isPhotoPreviewImage = false;
	private photoViewerPatchedDocument: any;
	private photoViewerOriginalTextLoader: ((...args: any[]) => any) | null = null;

	constructor(
		private digitalLogService: DigitalLogService,
		private documentsService: DocumentsService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private _sanitizer: DomSanitizer,
		private ngZone: NgZone,
	) {
		this.form = this.activatedRoute.snapshot.data.photos.form;

		if (this.activatedRoute.snapshot.data.photos && this.activatedRoute.snapshot.data.photos.photos && this.activatedRoute.snapshot.data.photos.photos.length > 0) {
			this.photos = this.activatedRoute.snapshot.data.photos.photos;
			this.filteredPhotos = this.photos;
		}

		this.selectedGroupId = this.activatedRoute.snapshot.queryParams['groupId'];

		if (this.form && this.form.fields && this.form.fields.length > 0) {
			this.digitalLogFormFields = this.form.fields
				.filter(x => !x.isTimeSlotCompletion)
				.map(x => ({
						id: x.digitalLogFormFieldId,
						fieldName: x.name,
						concatenatedName: x.selectionDigitalLogFieldId ? x.name?.concat(' ', this.form.fields.find(f => f.digitalLogFormFieldId === x.selectionDigitalLogFieldId && !f.isTimeSlotCompletion)?.name) : x.name,
						conditionalName: x.selectionDigitalLogFieldId ? this.form.fields.find(f => f.digitalLogFormFieldId === x.selectionDigitalLogFieldId && !f.isTimeSlotCompletion)?.name : null,
						digitalLogFormGroupId: x.digitalLogFormGroupId,
						selectionDigitalLogFieldId: x.selectionDigitalLogFieldId,
						listOrder: x.selectionDigitalLogFieldId ? this.form.fields.find(f => f.digitalLogFormFieldId === x.selectionDigitalLogFieldId && !f.isTimeSlotCompletion)?.listOrder : x.listOrder,
						conditionalListOrder: x.selectionDigitalLogFieldId ? x.listOrder : null }));

			if (this.digitalLogFormFields.find(f => !f.digitalLogFormGroupId))
			{
				this.groupedDigitalLogFormFields.push(
					{
						digitalLogFormGroupId : null,
						digitalLogFormGroupName: '-Global-',
						digitalLogFormFields: this.digitalLogFormFields.filter(f => !f.digitalLogFormGroupId)
					});
			}

			this.form.groups.forEach(gr => {
				this.groupedDigitalLogFormFields.push(
					{
						digitalLogFormGroupId : gr.digitalLogFormGroupId,
						digitalLogFormGroupName: gr.name,
						digitalLogFormFields: this.digitalLogFormFields.filter(f => f.digitalLogFormGroupId === gr.digitalLogFormGroupId)
					});
			});

			this.groupedDigitalLogFormFields.forEach(field => {
				field.digitalLogFormFields = sortBy(field.digitalLogFormFields, ['digitalLogFormGroupName', 'listOrder', 'concatenatedName', 'conditionalListOrder']);
			});
		}
	}

	public getGroupName(digitalLogFormFieldId: number): string | null{
		if (!digitalLogFormFieldId) {return null}

		return this.groupedDigitalLogFormFields.find(g => g.digitalLogFormFields.some(f => f.id === digitalLogFormFieldId))?.digitalLogFormGroupName;
	}

	public getSectionName(digitalLogFormFieldId: number): string | null{
		if (!digitalLogFormFieldId) {return null}
		return this.digitalLogFormFields.find(f => f.id === digitalLogFormFieldId)?.conditionalName;
	}

	public ngOnInit(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = false;
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.priorityNavigationBarActions = new Map<string, NavigationActionButton>();
		GlobalSettingsService.priorityNavigationBarActions.set('close_page', {
			action: () => this.close(),
			icon: 'close',
			listOrder: 1
		});
	}

	public close() {
		this.router.navigate(['../'], { relativeTo: this.activatedRoute });
	}

	public onClickAddPhoto(input) {
		input.click();
	}

	public onChangePhotos(files: File[], fileInput) {
		const filesToUpload = Object.values(files);

		//photo size max 10MB 
		const maxAllowedSize = 10 * 1024 * 1024;

		const isAnyPhotoExceedMaxSize = filesToUpload.some(file => {
			if (file.size > maxAllowedSize) {
				return true;
			}
			return false;
		});

		if (!isAnyPhotoExceedMaxSize && filesToUpload.length <= 10) {
			this.addPhotos(filesToUpload);
		}
		else {
			if (filesToUpload.length > 10) {
				this.addPhotosMessage = `Depending on your internet connection speed, you may experience delays due to the number of files (${filesToUpload.length}) selected.`;
			}
			if (isAnyPhotoExceedMaxSize) {
				this.addPhotosMessage = `Depending on your internet connection speed, you may experience delays due to the file size of the selected files.`;
			}
			if (filesToUpload.length > 10 && isAnyPhotoExceedMaxSize) {
				this.addPhotosMessage = `Depending on your internet connection speed, you may experience delays due to the number of files (${filesToUpload.length}) selected and file size.`;
			}

			this.addPhotosConfirmationModal.open(filesToUpload);
		}

		fileInput.value = '';
	}

	public onAddPhotosConfirmed(files) {
		this.isPhotosSaving = true;
		this.addPhotos(files);
	}

	public addPhotos(files) {
		GlobalSettingsService.isSecondaryLoaderLoading = true;
		this.addPhotosConfirmationModal.hide();

		this.digitalLogService
			.uploadFormPhotos(TokenHelper.getToken(this.activatedRoute), files, this.form.jobDigitalLogFormRequestId)
			.pipe(
				takeUntil(this.unsubscriber$)
			)
			.subscribe(
				() => {
					this.getPhotos();
				},
				() => {
					this.isPhotosSaving = false;
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					ToastService.pushToast({
						text: 'Could not add photos. Please try again!',
						icon: 'error',
						type: ToastType.Error
					});
				}
			);
	}

	public getPhotos() {
		this.digitalLogService.getFormPhotos(TokenHelper.getToken(this.activatedRoute), this.form.jobDigitalLogFormRequestId)
			.pipe(
				takeUntil(this.unsubscriber$)
			)
			.subscribe(
				result => {
					this.photos = result;
					this.onFilterPhotos();
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				},
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					ToastService.pushToast({
						text: 'Could not get photos. Please try again!',
						icon: 'error',
						type: ToastType.Error
					});
				}
			);
	}

	public onSavePhotoDetails(photo) {
		GlobalSettingsService.isSecondaryLoaderLoading = true;
		this.digitalLogService
			.saveFormPhoto(
				TokenHelper.getToken(this.activatedRoute),
				this.form.jobDigitalLogFormRequestId,
				{
					id: photo.id,
					jobDigitalLogFormRequestId: photo.jobDigitalLogFormRequestId,
					note: photo.updatedNote,
					digitalLogFormFieldId: photo.updatedDigitalLogFormFieldId,
				})
			.pipe(
				takeUntil(this.unsubscriber$)
			)
			.subscribe(
				() => {
					photo.digitalLogFormFieldId = photo.updatedDigitalLogFormFieldId;
					const field = this.digitalLogFormFields.find(x => x.id === photo.digitalLogFormFieldId);
					if (field) {
						photo.digitalLogFormField = field.fieldName;
					}
					photo.note = photo.updatedNote;
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				},
				() => { 
					GlobalSettingsService.isSecondaryLoaderLoading = false; 
					ToastService.pushToast({
						text: 'Could not update photo. Please try again!',
						icon: 'error',
						type: ToastType.Error
					});
				}
			);
	}

	public onDeletePhotoConfirmed(photo) {
		this.isDeleteDisabled = true;
		this.deletePhoto(photo)
	}

	public onSearchChange(searchValue) {
		this.search = searchValue;
		this.onFilterPhotos();
	}

	public onFromDateChange(date) {
		this.fromDate = null;
		if (date) {
			if (this.toDate) {
				if (date <= this.toDate) {
					this.fromDate = date;
				}
			} else {
				this.fromDate = date;
			}
		}
		this.onFilterPhotos();
	}

	public onToDateChange(date) {
		this.toDate = null
		if (date) {
			if (this.fromDate) {
				if (date >= this.fromDate) {
					this.toDate = date;
				}
			}
			else {
				this.toDate = date;
			}
		}
		this.onFilterPhotos();
	}

	private onFilterPhotos() {
		this.filteredPhotos = this.photos;

		if (this.search) {
			const searchValue = this.search.toLowerCase();
			this.filteredPhotos = this.filteredPhotos.filter(x => {
				if (this.compareSearch(x.digitalLogFormField, searchValue)) {
					return true;
				}
				if (this.compareSearch(x.note, searchValue)) {
					return true;
				}

				return false;
			});
		}

		if (this.fromDate) {
			const fromDateMoment = moment(this.formatDate(this.fromDate), this.dateFormat);
			this.filteredPhotos = this.filteredPhotos.filter(x => {
				if (x.createdOn) {
					const createdOnDate = this.formatDate(x.createdOn);
					const createdOnMoment = moment(createdOnDate, this.dateFormat);
					if (createdOnMoment.isSameOrAfter(fromDateMoment, 'day')) {
						return true;
					}
				}
				return false;
			})
		}

		if (this.toDate) {
			const toDateMoment = moment(this.formatDate(this.toDate), this.dateFormat);
			this.filteredPhotos = this.filteredPhotos.filter(x => {
				if (x.createdOn) {
					if (moment(this.formatDate(x.createdOn), this.dateFormat).isSameOrBefore(toDateMoment, 'day')) {
						return true;
					}
				}
				return false;
			})
		}
	}

	private compareSearch(value: string, search: string) {
		if (value) {
			return value.toLowerCase().includes(search);
		}
		return false;
	}

	public deletePhoto(photo) {
		GlobalSettingsService.isSecondaryLoaderLoading = true;
		this.deleteConfirmationModal.hide();
		this.digitalLogService.deleteFormPhoto(TokenHelper.getToken(this.activatedRoute), this.form.jobDigitalLogFormRequestId, photo.id)
			.pipe(
				takeUntil(this.unsubscriber$)
			)
			.subscribe(
				() => {
					this.getPhotos();
					this.isDeleteDisabled = false;
				},
				() => {
					this.isDeleteDisabled = false;
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					ToastService.pushToast({
						text: 'Could not delete photo. Please try again!',
						icon: 'error',
						type: ToastType.Error
					});
				}
			);
	}

	public loadPhotoPreview(photo: DigitalLogFormPhoto, event?: Event): void {
		if (event) {
			event.stopPropagation();
		}

		if (!photo?.documentId || !photo?.documentOid) {
			ToastService.pushToast({
				text: 'Photo preview is unavailable.',
				icon: 'error',
				type: ToastType.Error
			});
			return;
		}

		this.cancelPhotoPreviewRequest();
		this.isPhotoPreviewImage = this.isPhotoMediaImage(photo);
		this.releasePhotoViewerTextGuard();
		this.selectedDocumentDetails = null;
		this.photoPreviewModal.open();
		this.documentsService.getDocumentByOid(photo.documentOid, photo.documentId, false)
			.pipe(
				takeUntil(this.unsubscriber$),
				takeUntil(this.photoPreviewRequest$)
			)
			.subscribe(
				(response: HttpResponse<Blob>) => {
					this.selectedDocumentDetails = {
						documentId: photo.documentId,
						content: response.body,
						documentDisplayName: photo.name || photo.digitalLogFormField || 'Photo',
						documentDownloadName: photo.name || photo.digitalLogFormField || 'Photo',
						isFitToPage: true						
					};
				},
				() => {
					this.selectedDocumentDetails = null;
					this.isPhotoPreviewImage = false;
					ToastService.pushToast({
						text: 'Could not load photo preview.',
						icon: 'error',
						type: ToastType.Error
					});
					this.photoPreviewModal.hide();
				}
			);
	}

	onOpenPhotoDetails(photo) {
		photo.updatedDigitalLogFormFieldId = photo.digitalLogFormFieldId;
		photo.updatedNote = photo.note;
		
		this.availableGroupedDigitalLogFormFields = this.groupedDigitalLogFormFields;
	
		this.detailsModal.open(photo);
	}

	formatDate(date) {
		if (date) {
			return moment(date).format(this.dateFormat);
		}
		return '';
	}

	public imageThumnail(photo: DigitalLogFormPhoto): SafeResourceUrl | string {
		return photo.thumbnailB64 ? this._sanitizer.bypassSecurityTrustResourceUrl(`data:${photo.mediaType};base64, ${photo.thumbnailB64}`) : "../../../../../../assets/icons/no_photo.svg";
	}

	public ngOnDestroy(): void {
		GlobalSettingsService.priorityNavigationBarActions = null;
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
		this.cancelPhotoPreviewRequest();
		this.photoPreviewRequest$.complete();
		this.detachPhotoViewerListener();
	}

	private handlePhotoPreviewViewerRendered(uiInstance: any): void {
		if (!uiInstance) {
			return;
		}
		if (this.photoViewerUiInstance && this.photoViewerUiInstance !== uiInstance) {
			this.detachPhotoViewerListener();
		}
		this.photoViewerUiInstance = uiInstance;
		if (this.photoViewerUiInstance?.addEventListener) {
			this.photoViewerUiInstance.addEventListener('documentLoaded', this.photoViewerDocumentLoadedHandler);
		}

		if (this.photoViewerUiInstance?.closeElements) {
			this.photoViewerUiInstance.closeElements(['leftPanel', 'thumbnailsPanel']);
		}

		if (this.photoViewerUiInstance?.disableElements) {
			this.photoViewerUiInstance.disableElements(['leftPanelButton', 'thumbnailsPanelButton']);
		}

		this.applyMobilePhotoPreviewUiOverrides();
	}

	private handlePhotoPreviewModalHide(): void {
		this.cancelPhotoPreviewRequest();
		this.isPhotoPreviewImage = false;
		this.selectedDocumentDetails = null;
		this.detachPhotoViewerListener();
		this.photoViewerUiInstance = null;
		this.releasePhotoViewerTextGuard();
	}

	private cancelPhotoPreviewRequest(): void {
		if (!this.photoPreviewRequest$.closed) {
			this.photoPreviewRequest$.next();
		}
	}

	private detachPhotoViewerListener(): void {
		if (this.photoViewerUiInstance?.removeEventListener && this.photoViewerDocumentLoadedHandler) {
			this.photoViewerUiInstance.removeEventListener('documentLoaded', this.photoViewerDocumentLoadedHandler);
		}
	}

	private isPhotoMediaImage(photo: DigitalLogFormPhoto): boolean {
		const mediaType = photo?.mediaType?.toLowerCase() || '';
		if (mediaType.startsWith('image/')) {
			return true;
		}
		const candidateName = (photo?.name || photo?.digitalLogFormField || this.fallbackFileBaseName || '').toLowerCase();
		return /\.(png|jpe?g|gif|bmp|webp)$/.test(candidateName);
	}

	private getPhotoDocViewer(): any {
		const viewerComponentRef = this.photoPreviewViewer as any;
		const viewerInstance = viewerComponentRef?.wvInstance || null;
		const docViewerFromInstance = viewerInstance?.Core?.documentViewer || viewerInstance?.docViewer || null;
		const docViewerFromUi = this.photoViewerUiInstance?.docViewer || this.photoViewerUiInstance?.Core?.documentViewer || null;
		return docViewerFromInstance || docViewerFromUi || null;
	}

	private guardPhotoViewerTextRequests(): void {
		if (!this.isPhotoPreviewImage) {
			this.releasePhotoViewerTextGuard();
			return;
		}

		const docViewer = this.getPhotoDocViewer();
		const doc = docViewer?.getDocument && docViewer.getDocument();
		if (!doc || doc === this.photoViewerPatchedDocument) {
			return;
		}

		this.releasePhotoViewerTextGuard();

		const originalLoader = doc.Uh;
		if (typeof originalLoader !== 'function') {
			return;
		}

			doc.Uh = (pageIndex: number, callback?: (textData?: any) => void) => {
				const safeCallback = (textData?: any) => {
					if (!textData || typeof textData.tf !== 'function' || typeof textData.Kc !== 'string') {
						callback?.(this.createEmptyTextData());
						return;
					}
					callback?.(textData);
			};

			try {
				return originalLoader.call(doc, pageIndex, safeCallback);
			} catch {
				safeCallback();
				return null;
			}
		};

		this.photoViewerPatchedDocument = doc;
		this.photoViewerOriginalTextLoader = originalLoader;
	}

	private releasePhotoViewerTextGuard(): void {
		if (this.photoViewerPatchedDocument && this.photoViewerOriginalTextLoader) {
			this.photoViewerPatchedDocument.Uh = this.photoViewerOriginalTextLoader;
		}
		this.photoViewerPatchedDocument = null;
		this.photoViewerOriginalTextLoader = null;
	}

	private createEmptyTextData(): any {
		return {
			Kc: '',
			Hf: [],
			tf: () => [],
			ready: () => true
		};
	}

	private ensureMobileFitMode(): void {
		if (!this.isPhotoPreviewMobileMode()) {
			return;
		}

		const viewer = this.photoViewerUiInstance;
		const fitWidthMode = viewer?.FitMode?.FitWidth;

		try {
			if (viewer?.setFitMode && fitWidthMode) {
				viewer.setFitMode(fitWidthMode);
			} else if (viewer?.UI?.setFitMode && viewer?.UI?.FitMode?.FitWidth) {
				viewer.UI.setFitMode(viewer.UI.FitMode.FitWidth);
			}
		} catch {
		}
	}

	private applyMobilePhotoPreviewUiOverrides(): void {
		if (!this.isPhotoPreviewImage || !this.isPhotoPreviewMobileMode()) {
			this.removeMobilePhotoPreviewStyleOverrides();
			return;
		}

		const viewer = this.photoViewerUiInstance;
		if (!viewer?.disableElements) {
			return;
		}

		try {
			viewer.disableElements([
				'header',
				'toolsHeader',
				'textSelectButton',
				'selectToolButton',
				'panToolButton'
			]);
		} catch {
		}

		this.applyMobilePhotoPreviewStyleOverrides();
	}

	private isPhotoPreviewMobileMode(): boolean {
		if (GlobalUtilsService.isMobileDevice) {
			return true;
		}

		try {
			return window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
		} catch {
			return false;
		}
	}

	private applyMobilePhotoPreviewStyleOverrides(): void {
		const iframeDocument = this.photoViewerUiInstance?.iframeWindow?.document;
		if (!iframeDocument) {
			return;
		}

		const styleId = 'photoPreviewMobileOverrides';
		if (iframeDocument.getElementById(styleId)) {
			return;
		}

		const style = iframeDocument.createElement('style');
		style.id = styleId;
		style.textContent = `
			[data-element="documentContainer"],
			.DocumentContainer,
			.PageContainer,
			.pageContainer,
			.page-container {
				border: 0 !important;
				box-shadow: none !important;
				outline: 0 !important;
			}

			[data-element="documentContainer"] .page,
			[data-element="documentContainer"] canvas,
			.DocumentContainer .page,
			.DocumentContainer canvas,
			.PageContainer .page,
			.PageContainer canvas {
				border: 0 !important;
				box-shadow: none !important;
				outline: 0 !important;
			}
		`;

		(iframeDocument.head || iframeDocument.documentElement).appendChild(style);
	}

	private removeMobilePhotoPreviewStyleOverrides(): void {
		const iframeDocument = this.photoViewerUiInstance?.iframeWindow?.document;
		const styleElement = iframeDocument?.getElementById('photoPreviewMobileOverrides');
		if (styleElement?.parentNode) {
			styleElement.parentNode.removeChild(styleElement);
		}
	}
}